/**
 * Migration API - RTK Query endpoints for admin migration management
 *
 * This API provides endpoints for SiteAdmin users to manage the migration
 * of organizations and users from Azure AD B2C to Kinde authentication.
 *
 * @see docs/KINDE_MIGRATION_PLAN.md
 */
import { createApi } from '@reduxjs/toolkit/query/react'

import { createAuthenticatedBaseQuery } from './baseQueryWithReauth'

const apiVersion = 'v2'

export const migrationApi = createApi({
    reducerPath: 'migrationApi',
    baseQuery: createAuthenticatedBaseQuery(),
    tagTypes: ['Organizations', 'Users', 'MigrationStatus'],
    endpoints: (builder) => ({
        /**
         * Get all organizations with their full user list and per-user Kinde migration status.
         * @returns {Array<{organisationId: string, name: string, migratedToKinde: boolean, organisationUsers: Array}>}
         */
        getOrganizations: builder.query({
            query: () => `/${apiVersion}/auth/migration/organisations/users`,
            providesTags: (result) =>
                result
                    ? [
                          { type: 'Organizations', id: 'LIST' },
                          ...result.map(({ organisationId }) => ({ type: 'Organizations', id: organisationId })),
                      ]
                    : [{ type: 'Organizations', id: 'LIST' }],
        }),

        /**
         * Get all active (non-soft-deleted) users with their organisation memberships and Kinde migration status.
         * @returns {Array<{userId: string, email: string, name: string, organisationName: string, migrationStatus: 'Not Migrated' | 'Migrated'}>}
         */
        getUsers: builder.query({
            query: () => `/${apiVersion}/auth/migration/users`,
            providesTags: (result) =>
                result
                    ? [{ type: 'Users', id: 'LIST' }, ...result.map(({ userId }) => ({ type: 'Users', id: userId }))]
                    : [{ type: 'Users', id: 'LIST' }],
        }),

        /**
         * Determines which authentication provider (Kinde or Azure B2C)
         * a user should be redirected to based on their email address.
         * @param {string} email - User's email address
         * @returns {{provider: 'Kinde' | 'AzureAdB2C' | null}}
         */
        getAuthProvider: builder.query({
            query: (email) => `/${apiVersion}/auth/provider?email=${encodeURIComponent(email)}`,
        }),

        /**
         * Get migration statistics
         * @returns {{totalUsers: number, migratedUsers: number, pendingUsers: number, failedMigrations: number, migrationPercentage: number, isComplete: boolean}}
         */
        getMigrationStatus: builder.query({
            query: () => `/${apiVersion}/auth/migration/status`,
            providesTags: [{ type: 'MigrationStatus', id: 'STATUS' }],
        }),

        /**
         * Get list of failed migrations
         * @returns {Array<{userId: string, email: string, error: string, failedAt: string}>}
         */
        getFailedMigrations: builder.query({
            query: () => `/${apiVersion}/auth/migration/failed`,
            providesTags: [{ type: 'MigrationStatus', id: 'FAILED' }],
        }),

        /**
         * Migrate a single user from B2C to Kinde
         * @param {string} userId - User ID to migrate
         * @returns {{success: boolean, message?: string}}
         */
        migrateUser: builder.mutation({
            query: (userId) => ({
                url: `/${apiVersion}/auth/migration/user/${userId}`,
                method: 'POST',
            }),
            invalidatesTags: [
                { type: 'Users', id: 'LIST' },
                { type: 'MigrationStatus', id: 'STATUS' },
            ],
        }),

        /**
         * Migrate selected users to Kinde
         * @param {string[]} userIds - Array of user IDs to migrate
         * @returns {{success: boolean, migratedCount?: number, errors?: Array<{userId: string, error: string}>}}
         */
        migrateUsers: builder.mutation({
            query: (userIds) => ({
                url: `/${apiVersion}/auth/migration/users`,
                method: 'POST',
                body: { userIds },
            }),
            invalidatesTags: [
                { type: 'Users', id: 'LIST' },
                { type: 'MigrationStatus', id: 'STATUS' },
            ],
        }),

        /**
         * Migrate all users in a single organisation from B2C to Kinde
         * @param {string} organisationId - Organisation ID to migrate
         * @returns {{success: boolean, message?: string}}
         */
        migrateOrganisation: builder.mutation({
            query: (organisationId) => ({
                url: `/${apiVersion}/auth/migration/organisation/${organisationId}`,
                method: 'POST',
            }),
            invalidatesTags: [
                { type: 'Organizations', id: 'LIST' },
                { type: 'Users', id: 'LIST' },
                { type: 'MigrationStatus', id: 'STATUS' },
            ],
        }),

        /**
         * Migrate selected organisations to Kinde
         * @param {string[]} organisationIds - Array of organisation IDs to migrate
         * @returns {{success: boolean, migratedCount?: number, errors?: Array<{organisationId: string, error: string}>}}
         */
        migrateOrganizations: builder.mutation({
            query: (organisationIds) => ({
                url: `/${apiVersion}/auth/migration/organisations`,
                method: 'POST',
                body: { organisationIds },
            }),
            invalidatesTags: [
                { type: 'Organizations', id: 'LIST' },
                { type: 'Users', id: 'LIST' },
                { type: 'MigrationStatus', id: 'STATUS' },
            ],
        }),

        /**
         * Migrate all remaining unmigrated users from B2C to Kinde
         * @returns {{success: boolean, migratedCount?: number, errors?: Array<{userId: string, error: string}>}}
         */
        migrateAllUsers: builder.mutation({
            query: () => ({
                url: `/${apiVersion}/auth/migration/all`,
                method: 'POST',
            }),
            invalidatesTags: [
                { type: 'Organizations', id: 'LIST' },
                { type: 'Users', id: 'LIST' },
                { type: 'MigrationStatus', id: 'STATUS' },
            ],
        }),

        /**
         * Retry a failed migration for a specific user
         * @param {string} userId - User ID to retry migration for
         * @returns {{success: boolean, message?: string}}
         */
        retryMigration: builder.mutation({
            query: (userId) => ({
                url: `/${apiVersion}/auth/migration/user/${userId}/retry`,
                method: 'POST',
            }),
            invalidatesTags: [
                { type: 'Users', id: 'LIST' },
                { type: 'MigrationStatus', id: 'STATUS' },
                { type: 'MigrationStatus', id: 'FAILED' },
            ],
        }),

        /**
         * Rollback migration for a single user
         * @param {string} userId - User ID to rollback
         * @returns {{success: boolean, message?: string}}
         */
        rollbackUser: builder.mutation({
            query: (userId) => ({
                url: `/${apiVersion}/auth/migration/rollback/user/${userId}`,
                method: 'POST',
            }),
            invalidatesTags: [
                { type: 'Users', id: 'LIST' },
                { type: 'MigrationStatus', id: 'STATUS' },
            ],
        }),

        /**
         * Rollback migration for multiple users
         * @param {string[]} userIds - Array of user IDs to rollback
         * @returns {{success: boolean, rolledBackCount?: number, errors?: Array<{userId: string, error: string}>}}
         */
        rollbackUsers: builder.mutation({
            query: (userIds) => ({
                url: `/${apiVersion}/auth/migration/rollback/users`,
                method: 'POST',
                body: { userIds },
            }),
            invalidatesTags: [
                { type: 'Users', id: 'LIST' },
                { type: 'MigrationStatus', id: 'STATUS' },
            ],
        }),

        /**
         * Rollback migration for all users in an organisation
         * @param {string} organisationId - Organisation ID to rollback
         * @returns {{success: boolean, message?: string}}
         */
        rollbackOrganisation: builder.mutation({
            query: (organisationId) => ({
                url: `/${apiVersion}/auth/migration/rollback/organisation/${organisationId}`,
                method: 'POST',
            }),
            invalidatesTags: [
                { type: 'Organizations', id: 'LIST' },
                { type: 'Users', id: 'LIST' },
                { type: 'MigrationStatus', id: 'STATUS' },
            ],
        }),

        /**
         * Rollback migration for multiple organisations
         * @param {string[]} organisationIds - Array of organisation IDs to rollback
         * @returns {{success: boolean, rolledBackCount?: number, errors?: Array<{organisationId: string, error: string}>}}
         */
        rollbackOrganisations: builder.mutation({
            query: (organisationIds) => ({
                url: `/${apiVersion}/auth/migration/rollback/organisations`,
                method: 'POST',
                body: { organisationIds },
            }),
            invalidatesTags: [
                { type: 'Organizations', id: 'LIST' },
                { type: 'Users', id: 'LIST' },
                { type: 'MigrationStatus', id: 'STATUS' },
            ],
        }),
    }),
})

export const {
    // Queries
    useGetAuthProviderQuery,
    useGetFailedMigrationsQuery,
    useGetMigrationStatusQuery,
    useGetOrganizationsQuery,
    useGetUsersQuery,
    useLazyGetAuthProviderQuery,
    // Mutations - Migration
    useMigrateAllUsersMutation,
    useMigrateOrganisationMutation,
    useMigrateOrganizationsMutation,
    useMigrateUserMutation,
    useMigrateUsersMutation,
    useRetryMigrationMutation,
    // Mutations - Rollback
    useRollbackOrganisationMutation,
    useRollbackOrganisationsMutation,
    useRollbackUserMutation,
    useRollbackUsersMutation,
} = migrationApi
